import React, { useState, useEffect } from 'react';
import User from '../../../assets/SVGs/User';
import { formatPhoneNumber, formatZipCode } from '../../../utils/utils';

// US States array for dropdown
const US_STATES = [
  { code: 'AL', name: 'Alabama' },
  { code: 'AK', name: 'Alaska' },
  { code: 'AZ', name: 'Arizona' },
  { code: 'AR', name: 'Arkansas' },
  { code: 'CA', name: 'California' },
  { code: 'CO', name: 'Colorado' },
  { code: 'CT', name: 'Connecticut' },
  { code: 'DE', name: 'Delaware' },
  { code: 'FL', name: 'Florida' },
  { code: 'GA', name: 'Georgia' },
  { code: 'HI', name: 'Hawaii' },
  { code: 'ID', name: 'Idaho' },
  { code: 'IL', name: 'Illinois' },
  { code: 'IN', name: 'Indiana' },
  { code: 'IA', name: 'Iowa' },
  { code: 'KS', name: 'Kansas' },
  { code: 'KY', name: 'Kentucky' },
  { code: 'LA', name: 'Louisiana' },
  { code: 'ME', name: 'Maine' },
  { code: 'MD', name: 'Maryland' },
  { code: 'MA', name: 'Massachusetts' },
  { code: 'MI', name: 'Michigan' },
  { code: 'MN', name: 'Minnesota' },
  { code: 'MS', name: 'Mississippi' },
  { code: 'MO', name: 'Missouri' },
  { code: 'MT', name: 'Montana' },
  { code: 'NE', name: 'Nebraska' },
  { code: 'NV', name: 'Nevada' },
  { code: 'NH', name: 'New Hampshire' },
  { code: 'NJ', name: 'New Jersey' },
  { code: 'NM', name: 'New Mexico' },
  { code: 'NY', name: 'New York' },
  { code: 'NC', name: 'North Carolina' },
  { code: 'ND', name: 'North Dakota' },
  { code: 'OH', name: 'Ohio' },
  { code: 'OK', name: 'Oklahoma' },
  { code: 'OR', name: 'Oregon' },
  { code: 'PA', name: 'Pennsylvania' },
  { code: 'RI', name: 'Rhode Island' },
  { code: 'SC', name: 'South Carolina' },
  { code: 'SD', name: 'South Dakota' },
  { code: 'TN', name: 'Tennessee' },
  { code: 'TX', name: 'Texas' },
  { code: 'UT', name: 'Utah' },
  { code: 'VT', name: 'Vermont' },
  { code: 'VA', name: 'Virginia' },
  { code: 'WA', name: 'Washington' },
  { code: 'WV', name: 'West Virginia' },
  { code: 'WI', name: 'Wisconsin' },
  { code: 'WY', name: 'Wyoming' }
];

interface DonorInformationProps {
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber: string;
  streetAddress: string;
  city: string;
  state: string;
  zipCode: string;
  specialDesignation?: string;
  onFieldChange: (field: string, value: string) => void;
  errors: {
    firstName?: string;
    lastName?: string;
    email?: string;
    phoneNumber?: string;
    streetAddress?: string;
    city?: string;
    state?: string;
    zipCode?: string;
  };
  onValidationChange?: (isValid: boolean) => void;
}

// Email validation rules
const EMAIL_VALIDATION_RULES = {
  // Basic format check
  format: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
  
  // More comprehensive regex for email validation
  comprehensive: /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/,
  
  // Check for common disposable email domains
  disposableDomains: [
    '10minutemail.com', 'guerrillamail.com', 'tempmail.org', 'mailinator.com',
    'throwawayemail.com', 'temp-mail.org', 'sharklasers.com', 'getairmail.com',
    'mailnesia.com', 'yopmail.com', 'getnada.com', 'maildrop.cc', 'mailinator.net'
  ]
};

const DonorInformation: React.FC<DonorInformationProps> = ({
  firstName,
  lastName,
  email,
  phoneNumber,
  streetAddress,
  city,
  state,
  zipCode,
  specialDesignation = '',
  onFieldChange,
  errors,
  onValidationChange
}) => {
  const [emailValidation, setEmailValidation] = useState({
    isValid: false,
    isTouched: false,
    errors: [] as string[]
  });

  const handleInputChange = (field: string, value: string) => {
    onFieldChange(field, value);
  };

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formattedValue = formatPhoneNumber(e.target.value);
    onFieldChange('phoneNumber', formattedValue);
  };

  const handleZipCodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formattedValue = formatZipCode(e.target.value);
    onFieldChange('zipCode', formattedValue);
  };

  const getPhoneDisplayValue = (): string => {
    if (!phoneNumber) return '';
    return phoneNumber;
  };

  // Email validation function
  const validateEmail = (emailValue: string): string[] => {
    const validationErrors: string[] = [];
    
    if (!emailValue.trim()) {
      validationErrors.push('Email is required');
      return validationErrors;
    }

    // Check basic format
    if (!EMAIL_VALIDATION_RULES.format.test(emailValue)) {
      validationErrors.push('Please enter a valid email address');
      return validationErrors;
    }

    // Check comprehensive format
    if (!EMAIL_VALIDATION_RULES.comprehensive.test(emailValue)) {
      validationErrors.push('Email format is invalid');
      return validationErrors;
    }

    // Check length
    if (emailValue.length > 254) {
      validationErrors.push('Email is too long (maximum 254 characters)');
    }

    // Check local part length (before @)
    const localPart = emailValue.split('@')[0];
    if (localPart.length > 64) {
      validationErrors.push('Email username is too long (maximum 64 characters)');
    }

    // Check for consecutive dots
    if (emailValue.includes('..')) {
      validationErrors.push('Email cannot contain consecutive dots');
    }

    // Check for dots at start/end of local part
    if (localPart.startsWith('.') || localPart.endsWith('.')) {
      validationErrors.push('Email username cannot start or end with a dot');
    }

    // Check for spaces
    if (emailValue.includes(' ')) {
      validationErrors.push('Email cannot contain spaces');
    }

    // Check for valid characters in local part
    const validLocalPartRegex = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+$/;
    if (!validLocalPartRegex.test(localPart)) {
      validationErrors.push('Email username contains invalid characters');
    }

    // Check for common disposable domains
    const domain = emailValue.split('@')[1]?.toLowerCase();
    // if (domain && EMAIL_VALIDATION_RULES.disposableDomains.includes(domain)) {
    //   validationErrors.push('Please use a valid email address (disposable emails not allowed)');
    // }

    // Check for valid TLD
    const tld = domain?.split('.').pop();
    if (tld && tld.length < 2) {
      validationErrors.push('Email domain appears to be invalid');
    }

    // Check for minimum domain length
    if (domain && domain.length < 3) {
      validationErrors.push('Email domain is too short');
    }

    // Check for valid domain format (no consecutive hyphens, no hyphens at start/end)
    if (domain && (domain.includes('--') || domain.startsWith('-') || domain.endsWith('-'))) {
      validationErrors.push('Email domain format is invalid');
    }

    return validationErrors;
  };

  // Handle email input change with validation
  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const emailValue = e.target.value;
    onFieldChange('email', emailValue);
    
    // Only validate if user has interacted with the field
    if (emailValidation.isTouched || emailValue.length > 0) {
      const emailErrors = validateEmail(emailValue);
      const isValid = emailErrors.length === 0;
      
      setEmailValidation({
        isValid,
        isTouched: true,
        errors: emailErrors
      });
    }
  };

  // Handle email blur (when user leaves the field)
  const handleEmailBlur = () => {
    if (!emailValidation.isTouched) {
      setEmailValidation(prev => ({ ...prev, isTouched: true }));
    }
    
    const emailErrors = validateEmail(email);
    const isValid = emailErrors.length === 0;
    
    setEmailValidation({
      isValid,
      isTouched: true,
      errors: emailErrors
    });
  };

  // Notify parent component about email validation changes
  useEffect(() => {
    if (onValidationChange) {
      onValidationChange(emailValidation.isValid);
    }
  }, [emailValidation.isValid, onValidationChange]);

  return (
    <div className="bg-white rounded-xl shadow-xl p-8 w-full">
      {/* Header */}
      <div className="flex items-center gap-1 mb-2">
        <div className="w-8 h-8 rounded-full flex items-center justify-center">
          <User width={32} height={32} />
        </div>
        <h1 className="text-2xl font-bold text-gray-800">Donor Information</h1>
      </div>
      
      <p className="text-sm text-gray-600 mb-6">All fields marked with * are required</p>

      {/* Form */}
      <form onSubmit={(e) => e.preventDefault()} className="space-y-6">
        {/* First Name and Last Name - Two Column Layout */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label htmlFor="firstName" className="block text-sm font-semibold text-gray-700 mb-2">
              First Name *
            </label>
            <input
              type="text"
              id="firstName"
              name="firstName"
              value={firstName}
              onChange={(e) => handleInputChange('firstName', e.target.value)}
              className={`w-full px-4 py-3 border-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all ${
                errors.firstName ? 'border-red-500 bg-red-50' : 'border-gray-300 bg-white focus:bg-white'
              }`}
              placeholder="First Name"
            />
            {errors.firstName && (
              <span className="text-red-500 text-sm mt-1 block">{errors.firstName}</span>
            )}
          </div>

          <div>
            <label htmlFor="lastName" className="block text-sm font-semibold text-gray-700 mb-2">
              Last Name *
            </label>
            <input
              type="text"
              id="lastName"
              name="lastName"
              value={lastName}
              onChange={(e) => handleInputChange('lastName', e.target.value)}
              className={`w-full px-4 py-3 border-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all ${
                errors.lastName ? 'border-red-500 bg-red-50' : 'border-gray-300 bg-white focus:bg-white'
              }`}
              placeholder="Last Name"
            />
            {errors.lastName && (
              <span className="text-red-500 text-sm mt-1 block">{errors.lastName}</span>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Email Address */}
          <div>
            <label htmlFor="email" className="block text-sm font-semibold text-gray-700 mb-2">
              Email Address *
            </label>
            <div className="relative">
              <input
                type="email"
                id="email"
                name="email"
                value={email}
                onChange={handleEmailChange}
                onBlur={handleEmailBlur}
                className={`w-full px-4 py-3 pr-10 border-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all ${
                  (emailValidation.isTouched && emailValidation.errors.length > 0) ? 'border-red-500 bg-red-50' : 
                  (emailValidation.isTouched && emailValidation.isValid && email) ? 'border-green-500 bg-green-50' :
                  'border-gray-300 bg-white focus:bg-white'
                }`}
                placeholder="Email"
              />
              {/* Validation status icon */}
              {emailValidation.isTouched && email && (
                <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                  {emailValidation.isValid ? (
                    <svg className="w-5 h-5 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                  ) : (
                    <svg className="w-5 h-5 text-red-500" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                  )}
                </div>
              )}
            </div>
            <p className="text-sm text-gray-500 mt-1">For your donation receipt</p>
            
            {/* Email validation errors */}
            {emailValidation.isTouched && emailValidation.errors.length > 0 && (
              <div className="mt-2 space-y-1">
                {emailValidation.errors.map((error, index) => (
                  <span key={index} className="text-red-500 text-sm block flex items-center">
                    <svg className="w-4 h-4 mr-1 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                    {error}
                  </span>
                ))}
              </div>
            )}
            
            {/* Show success state when email is valid */}
            {emailValidation.isTouched && emailValidation.isValid && email && (
              <span className="text-green-600 text-sm mt-1 block flex items-center">
                <svg className="w-4 h-4 mr-1 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                Valid email address
              </span>
            )}
          </div>
          {/* Phone Number */}
          <div>
            <label htmlFor="phoneNumber" className="block text-sm font-semibold text-gray-700 mb-2">
              Phone Number *
            </label>
            <div className="relative">
              <div className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-500 font-medium">
                +1
              </div>
              <input
                type="tel"
                id="phoneNumber"
                name="phoneNumber"
                value={getPhoneDisplayValue()}
                onChange={handlePhoneChange}
                className={`w-full pl-12 pr-4 py-3 border-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all ${
                  errors.phoneNumber ? 'border-red-500 bg-red-50' : 'border-gray-300 bg-white focus:bg-white'
                }`}
                placeholder="(555) 123-4567"
                maxLength={12} // XXX-XXX-XXXX format
              />
            </div>
            <p className="text-sm text-gray-500 mt-1">Format: XXX-XXX-XXXX</p>
            {errors.phoneNumber && (
              <span className="text-red-500 text-sm mt-1 block">{errors.phoneNumber}</span>
            )}
          </div>
        </div>

        {/* Address Fields */}
        <div className="space-y-6">
          {/* Street Address - Full Width */}
          <div>
            <label htmlFor="streetAddress" className="block text-sm font-semibold text-gray-700 mb-2">
              Street Address *
            </label>
            <input
              type="text"
              id="streetAddress"
              name="streetAddress"
              value={streetAddress}
              onChange={(e) => handleInputChange('streetAddress', e.target.value)}
              className={`w-full px-4 py-3 border-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all ${
                errors.streetAddress ? 'border-red-500 bg-red-50' : 'border-gray-300 bg-white focus:bg-white'
              }`}
              placeholder="123 Main Street"
            />
            {errors.streetAddress && (
              <span className="text-red-500 text-sm mt-1 block">{errors.streetAddress}</span>
            )}
          </div>

          {/* City, State, ZIP Code - Three Column Layout */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div>
              <label htmlFor="city" className="block text-sm font-semibold text-gray-700 mb-2">
                City *
              </label>
              <input
                type="text"
                id="city"
                name="city"
                value={city}
                onChange={(e) => handleInputChange('city', e.target.value)}
                className={`w-full px-4 py-3 border-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all ${
                  errors.city ? 'border-red-500 bg-red-50' : 'border-gray-300 bg-white focus:bg-white'
                }`}
                placeholder="City"
              />
              {errors.city && (
                <span className="text-red-500 text-sm mt-1 block">{errors.city}</span>
              )}
            </div>

            <div>
              <label htmlFor="state" className="block text-sm font-semibold text-gray-700 mb-2">
                State *
              </label>
              <select
                id="state"
                name="state"
                value={state}
                onChange={(e) => handleInputChange('state', e.target.value)}
                className={`w-full px-4 py-3 border-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all ${
                  errors.state ? 'border-red-500 bg-red-50' : 'border-gray-300 bg-white focus:bg-white'
                }`}
              >
                <option value="">Select State</option>
                {US_STATES.map((stateOption) => (
                  <option key={stateOption.code} value={stateOption.code}>
                    {stateOption.code} - {stateOption.name}
                  </option>
                ))}
              </select>
              {errors.state && (
                <span className="text-red-500 text-sm mt-1 block">{errors.state}</span>
              )}
            </div>

            <div>
              <label htmlFor="zipCode" className="block text-sm font-semibold text-gray-700 mb-2">
                ZIP Code *
              </label>
              <input
                type="text"
                id="zipCode"
                name="zipCode"
                value={zipCode}
                onChange={handleZipCodeChange}
                className={`w-full px-4 py-3 border-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all ${
                  errors.zipCode ? 'border-red-500 bg-red-50' : 'border-gray-300 bg-white focus:bg-white'
                }`}
                placeholder="12345"
                maxLength={10}
              />
              {errors.zipCode && (
                <span className="text-red-500 text-sm mt-1 block">{errors.zipCode}</span>
              )}
            </div>
          </div>
        </div>

        {/* Special Designation */}
        <div>
          <label htmlFor="specialDesignation" className="block text-sm font-semibold text-gray-700 mb-2">
            Special Designation (optional)
          </label>
          <textarea
            id="specialDesignation"
            name="specialDesignation"
            value={specialDesignation}
            onChange={(e) => handleInputChange('specialDesignation', e.target.value)}
            rows={3}
            className="w-full px-4 py-3 border-2 border-gray-300 bg-white rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:bg-white transition-all resize-none"
            placeholder="In memory of, in honor of, or specific program support..."
          />
        </div>
      </form>
    </div>
  );
};

export default DonorInformation; 